import React, { useState, useEffect, useRef } from 'react';

const CustomerSearchSelect = ({ customers = [], value, onChange, disabled, placeholder = 'Search customers...', required = true }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // When value changes externally (e.g. reset), sync the display text
  useEffect(() => {
    if (!value) {
      setQuery('');
    } else {
      const match = customers.find(c => String(c.id) === String(value));
      if (match) setQuery(`${match.first_name} ${match.last_name}`);
    }
  }, [value, customers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        // If nothing was selected, clear the query
        if (!value) setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const sorted = [...customers].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  );

  const filtered = query.trim()
    ? sorted.filter(c =>
        `${c.first_name} ${c.last_name} ${c.phone || ''}`.toLowerCase().includes(query.toLowerCase())
      )
    : sorted;

  const handleInput = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange(''); // clear selection when input cleared
  };

  const handleSelect = (c) => {
    setQuery(`${c.first_name} ${c.last_name}`);
    setOpen(false);
    onChange(String(c.id));
  };

  const handleFocus = () => {
    if (!disabled) setOpen(true);
  };

  return (
    <div ref={containerRef} className="customer-search-select" style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        placeholder={placeholder}
        value={query}
        onChange={handleInput}
        onFocus={handleFocus}
        disabled={disabled}
        autoComplete="off"
        required={required && !value}
      />
      {open && !disabled && filtered.length > 0 && (
        <ul style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#1a1a1a',
          border: '1px solid #333',
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          maxHeight: '220px',
          overflowY: 'auto',
          zIndex: 1000,
          margin: 0,
          padding: 0,
          listStyle: 'none'
        }}>
          {filtered.map(c => (
            <li
              key={c.id}
              onMouseDown={() => handleSelect(c)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                color: '#e5e5e5',
                fontSize: '14px',
                borderBottom: '1px solid #2a2a2a',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#2a2a2a'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontWeight: 500 }}>{c.first_name} {c.last_name}</span>
            </li>
          ))}
        </ul>
      )}
      {open && !disabled && filtered.length === 0 && query.trim() && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#1a1a1a',
          border: '1px solid #333',
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          padding: '10px 14px',
          color: '#888',
          fontSize: '14px',
          zIndex: 1000
        }}>
          No customers found
        </div>
      )}
    </div>
  );
};

export default CustomerSearchSelect;
